"use client";

import { useState } from "react";
import { inputClass, labelClass } from "@/components/form";
import { Segmented } from "@/components/Segmented";
import { StampTool } from "@/components/StampTool";
import { outputName } from "@/lib/files";
import {
  DEFAULT_PAGE_NUMBERS,
  pageLabels,
  STAMP_MARGIN,
  type NumberFormat,
  type NumberPosition,
  type PageNumberOptions,
} from "@/lib/pdf/stamp-options";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { pageNumbersFile } from "@/workers/pdf-client";

const tool = getTool("page-numbers")!;

const POSITIONS: { value: NumberPosition; label: string }[] = [
  { value: "top-left", label: "Top left" },
  { value: "top-center", label: "Top centre" },
  { value: "top-right", label: "Top right" },
  { value: "bottom-left", label: "Bottom left" },
  { value: "bottom-center", label: "Bottom centre" },
  { value: "bottom-right", label: "Bottom right" },
];

const FORMATS: { value: NumberFormat; label: string }[] = [
  { value: "n", label: "1" },
  { value: "page-n", label: "Page 1" },
  { value: "page-n-of-total", label: "Page 1 of 9" },
  { value: "n-of-total", label: "1 / 9" },
];

/** Page numbers (Phase 5, browser). */
export function PageNumbersTool() {
  const [o, setO] = useState<PageNumberOptions>(DEFAULT_PAGE_NUMBERS);
  const set = (patch: Partial<PageNumberOptions>) =>
    setO((old) => ({ ...old, ...patch }));
  const [startText, setStartText] = useState("1");

  return (
    <StampTool
      tool={tool}
      button="Add page numbers"
      busyLabel="Numbering the pages…"
      run={(file) => pageNumbersFile(file, o)}
      resultName={(file) => outputName(file.name, "numbered", "pdf")}
      overlay={({ size }) => {
        const label = o.skipFirst ? null : pageLabels(1, o)[0];
        if (!label) return null;
        const [v, h] = o.position.split("-");
        const edge = `${(STAMP_MARGIN / size.width) * 100}cqw`;
        return (
          <span
            aria-hidden="true"
            className="absolute whitespace-nowrap text-ink"
            style={{
              fontSize: `${(o.size / size.width) * 100}cqw`,
              fontFamily: "Helvetica, Arial, sans-serif",
              [v === "top" ? "top" : "bottom"]: `calc(${edge} - 0.2em)`,
              ...(h === "left"
                ? { left: edge }
                : h === "right"
                  ? { right: edge }
                  : { left: "50%", transform: "translateX(-50%)" }),
            }}
          >
            {label}
          </span>
        );
      }}
    >
      <div
        role="group"
        aria-label="Position"
        className="grid grid-cols-3 gap-1.5"
      >
        {POSITIONS.map((p) => (
          <button
            key={p.value}
            type="button"
            aria-pressed={o.position === p.value}
            onClick={() => set({ position: p.value })}
            className={cn(
              "min-h-11 rounded-lg border-[1.5px] px-2 text-[13px] text-ink",
              o.position === p.value
                ? "border-brand bg-brand-tint font-semibold"
                : "border-line-strong hover:bg-sand",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={labelClass}>
          Style
          <select
            className={inputClass}
            value={o.format}
            onChange={(e) => set({ format: e.target.value as NumberFormat })}
          >
            {FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          First number
          <input
            className={inputClass}
            inputMode="numeric"
            value={startText}
            onChange={(e) => {
              setStartText(e.target.value);
              const n = Number(e.target.value);
              if (Number.isInteger(n) && n >= 0 && n <= 99999)
                set({ start: n });
            }}
          />
        </label>
      </div>
      <Segmented
        label="Size"
        options={[
          { value: "9", label: "Small" },
          { value: "11", label: "Medium" },
          { value: "14", label: "Large" },
        ]}
        value={String(o.size) as "9" | "11" | "14"}
        onChange={(size) => set({ size: Number(size) })}
        stretch
      />
      <label className="flex min-h-11 items-center justify-between gap-3 text-[15px] font-semibold">
        Leave the first page without a number (cover)
        <input
          type="checkbox"
          className="size-[22px] accent-brand"
          checked={o.skipFirst}
          onChange={(e) => set({ skipFirst: e.target.checked })}
        />
      </label>
    </StampTool>
  );
}
